import _ from 'lodash';
import { useSearchParams } from 'next/navigation';
import querystring from 'query-string';
import { FC, SyntheticEvent, useState } from 'react';
import { useIntl } from 'react-intl';

import useForm, { stringFromModelValue, validation } from '@calm-web/use-form';

import { EnvConfig } from '@/env_config';
import { useAnalytics } from '@/hooks/analytics/useAnalytics';
import {
	getB2bCopyTestVariant,
	setSelfserveLeadRerouteTestVariant,
} from '@/utils/analytics/b2bSignupVariant';

import { ErrorBanner, SignUpForm, StyledButton } from '../styles';
import { CompanyFormFields } from '../types';
import {
	calculatingSelectedContractedLives,
	FORM_LOG_STRING,
	performTeamsFormValidation,
	ValidActions,
} from '../utils';
import messages from './messages';
import { Input, StyledFormSelect } from './styles';
import {
	CompanyInfoFormProps,
	CompanyProps,
	CompanySize,
	ContactDetails,
	FormattedUserEnteredDetails,
	MarketoForm,
} from './types';

const sizeSelectOptions: CompanySize[] = ['5-300', '301-2,000', '2,001-8,000', '8,001+'];

declare const MktoForms2: {
	loadForm: (
		baseUrl: string,
		munchkinId: string,
		formId: number,
		callback: (form: MarketoForm) => void,
	) => void;
};

function sendContactDataToMarketo(contactDetails: ContactDetails): void {
	if (typeof MktoForms2 === 'undefined') return;

	MktoForms2.loadForm(
		EnvConfig.marketoBaseUrl,
		EnvConfig.marketoMunchkinId,
		parseInt(EnvConfig.marketoContactFormId),
		form => {
			form
				.setValues({
					Email: contactDetails.email,
					FirstName: contactDetails.first_name,
					LastName: contactDetails.last_name,
					Company: contactDetails.companyName,
					Title: contactDetails.job_title,
					company_size__c: contactDetails.companySize,
					utm_campaign__c: contactDetails.utm_campaign ?? '',
					utm_medium__c: contactDetails.utm_medium ?? '',
					utm_source__c: contactDetails.utm_source ?? '',
					B2BTestVariant__c: contactDetails.ctaCopyVariant,
					...(contactDetails.signUpVariant && { B2BSignUpVariant__c: contactDetails.signUpVariant }),
				})
				.onSuccess(() => {
					// false overrides default marketo behavior of attempting a redirect
					return false;
				})
				.submittable(true)
				.submit();
		},
	);
}

const Company: FC<CompanyProps> = ({ navigateTo, defaultValues, infoFields, coveredLives }) => {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const searchParams = useSearchParams();
	const queryString = searchParams?.toString() ?? '';
	const query = querystring.parse(queryString);
	const { utm_campaign, utm_medium, utm_source } = query;
	const [error, setError] = useState<string | null>(null);
	const [showValidationCompanyName, setShowValidationCompanyName] = useState<boolean | undefined>();
	const [showValidationCompanySize, setShowValidationCompanySize] = useState<boolean | undefined>();
	const [showValidationRole, setShowValidationRole] = useState<boolean | undefined>();

	const selectOptions = [
		formatMessage(messages.companyFormRoleHumanResources),
		formatMessage(messages.companyFormRoleHealthWellness),
		formatMessage(messages.companyFormRoleBenefits),
		formatMessage(messages.companyFormRoleExec),
		formatMessage(messages.companyFormRoleBroker),
		formatMessage(messages.companyFormRoleOther),
	];

	const formProps: CompanyInfoFormProps = useForm('infoForm', {
		initialModel: {
			companyName: defaultValues?.companyName ?? '',
			companySize: defaultValues?.companySize ?? '',
			role: defaultValues?.role ?? '',
		},
		validation: {
			companyName: validation.validateOrFail([
				{
					rules: [validation.required, validation.minLength(2)],
					errorResult: formatMessage(messages.companyFormCompanyNameError),
				},
			]),
			companySize: validation.validateOrFail([
				{
					rules: [validation.required],
					errorResult: formatMessage(messages.companyFormCompanySizeError),
				},
			]),
			role: validation.validateOrFail([
				{
					rules: [validation.required],
					errorResult: formatMessage(messages.companyFormRoleError),
				},
			]),
		},
	});

	const getFormattedUserEnteredFieldValues = (): FormattedUserEnteredDetails | undefined => {
		if (!infoFields || !('email' in infoFields)) return;

		const companyName = stringFromModelValue(formProps.model.companyName) as string;
		const companySize = stringFromModelValue(formProps.model.companySize) as CompanySize;
		const role = stringFromModelValue(formProps.model.role) as string;

		const { email, firstName, lastName } = infoFields;

		return {
			email,
			first_name: firstName,
			last_name: lastName,
			companyName,
			company_size: companySize,
			job_title: role,
		};
	};

	const sendEventWithFormProps = (eventName: string): void => {
		const userEnteredFields = getFormattedUserEnteredFieldValues();
		if (userEnteredFields) {
			logEvent(eventName, {
				...userEnteredFields,
				location: 'company',
				variant: '7-16-24',
				selectedCoveredLives: coveredLives ?? '',
				selectedContractedLives: calculatingSelectedContractedLives(coveredLives ?? 0),
				companySize: stringFromModelValue(formProps.model.companySize) ?? '',
			});
		}
	};

	const _performValidation = (action: ValidActions): boolean =>
		performTeamsFormValidation({
			action,
			formProps,
			setError,
			logEvent,
			location: 'company',
			coveredLives,
		});

	const onSubmit = (e: SyntheticEvent): void => {
		e.preventDefault();

		const isValid = _performValidation('submit');
		if (!isValid) {
			return;
		}

		const alreadySubmittedForm = !_.isEmpty(defaultValues);
		const userEnteredFields = getFormattedUserEnteredFieldValues();
		if (!userEnteredFields) return;

		if (!alreadySubmittedForm) {
			sendEventWithFormProps('Teams : Contact : Created');
			setSelfserveLeadRerouteTestVariant();

			const ctaCopyVariant = getB2bCopyTestVariant();
			const signUpVariant = 'SoftUpsellIntercept';
			sendContactDataToMarketo({
				...userEnteredFields,
				ctaCopyVariant,
				utm_campaign,
				utm_medium,
				utm_source,
				companySize: userEnteredFields.company_size,
				signUpVariant,
			});
		}

		if (alreadySubmittedForm || error === null) {
			logEvent(FORM_LOG_STRING, {
				location: 'company',
				action: 'submit',
				status: 'success',
				selectedCoveredLives: coveredLives ?? '',
				variant: '7-16-24',
				selectedContractedLives: calculatingSelectedContractedLives(coveredLives ?? 0),
				companySize: stringFromModelValue(formProps.model.companySize) ?? '',
			});

			navigateTo('payment', { ...formProps.model } as CompanyFormFields);
		}
	};

	const onPress = (): void => {
		if (!formProps.dirtyState.companyName?.hasChanged || !formProps.validation.fields.companyName?.isValid) {
			setShowValidationCompanyName(true);
		}
		if (!formProps.dirtyState.companySize?.hasChanged || !formProps.validation.fields.companySize?.isValid) {
			setShowValidationCompanySize(true);
		}
		if (!formProps.dirtyState.role?.hasChanged || !formProps.validation.fields.role?.isValid) {
			setShowValidationRole(true);
		}
	};

	return (
		<>
			{error && (
				<ErrorBanner>
					{formatMessage(messages.infoFormErrorBanner, {
						errorCount: error === 'one' ? `is ${error} item` : `are ${error} items`,
					})}
				</ErrorBanner>
			)}
			<SignUpForm onSubmit={onSubmit} id="company-form" data-testid="company-form">
				<Input
					{...formProps.bindWithErrorProps('companyName', 'text', showValidationCompanyName)}
					label={formatMessage(messages.companyFormCompanyNameLabel)}
					data-testid="get-setup-company-name"
					onFocus={() => {
						setError(null);
					}}
					onBlur={() => {
						_performValidation('blur');
						setShowValidationCompanyName(true);
					}}
				/>
				<StyledFormSelect
					{...formProps.bindWithErrorProps('companySize', 'select', showValidationCompanySize)}
					aria-label={formatMessage(messages.companyFormCompanySizePlaceholder)}
					placeholder={formatMessage(messages.companyFormCompanySizePlaceholder)}
					options={sizeSelectOptions}
					data-testid="get-setup-company-size"
					onBlur={() => {
						setError(null);
						_performValidation('blur');
						setShowValidationCompanySize(true);
					}}
				/>
				<StyledFormSelect
					{...formProps.bindWithErrorProps('role', 'select', showValidationRole)}
					aria-label={formatMessage(messages.companyFormRolePlaceholder)}
					placeholder={formatMessage(messages.companyFormRolePlaceholder)}
					options={selectOptions}
					data-testid="get-setup-company-role"
					onBlur={() => {
						setError(null);
						_performValidation('blur');
						setShowValidationRole(true);
					}}
				/>
				<StyledButton
					type="submit"
					backgroundColor="gradientCalmBrand"
					data-testid="get-setup-next-button"
					onPress={onPress}
				>
					{formatMessage(messages.companyFormCtaCopy)}
				</StyledButton>
			</SignUpForm>
		</>
	);
};

export default Company;
